import { PropsWithChildren, useEffect, useMemo, useState } from 'react';

import { AssessmentPreview, AssessmentPreviewType } from '@opositandopn/ui';
import {
  FcAcceptDatabase,
  FcAddDatabase,
  FcDataBackup,
  FcDeleteDatabase,
} from 'react-icons/fc';
import { useWindowSize } from 'usehooks-ts';

import { Loading } from '../../../../components/core/Loading';
import { AdminWidthResolution } from '../../../../constants/app';
import { useProfile } from '../../../../context/ProfileContext';
import { BaseComponentProps } from '../../../../types/base-component.types';
import {
  useGetMultipleChoiceAssessment,
  useGetMultipleChoiceAssessmentMetrics,
} from '../../api/useMultipleChoiceAssessments';
import { resolveDifficulty } from '../../difficulty';
import { AssessmentScoreboard } from '../assessment-scoreboard/AssessmentScoreboard';
import { MultipleChoiceAssessmentEditor } from '../multiple-choice-assessment-editor/MultipleChoiceAssessmentEditor';
import { MultipleChoiceAssessmentQuestionsPreviewList } from '../multiple-choice-assessment-questions-preview-list/MultipleChoiceAssessmentQuestionsPreviewList';

export type ContainerProps = BaseComponentProps & {
  subjectId: string;
  assessmentId: string;
  onClose: () => void;
};

function MultipleChoiceAssessmentPreview({
  subjectId,
  assessmentId,
  onClose,
  children,
}: PropsWithChildren<ContainerProps>) {
  const { profile } = useProfile();
  const { width } = useWindowSize();

  const [showEdit, setShowEdit] = useState(false);
  const [showViewQuestions, setShowViewQuestions] = useState(false);

  const {
    data: response,
    isLoading,
    isError,
  } = useGetMultipleChoiceAssessment({
    subjectId,
    assessmentId,
  });

  const { data: metricsResponse, isPending: isMetricsPending } =
    useGetMultipleChoiceAssessmentMetrics({
      subjectId,
      assessmentId,
    });

  useEffect(() => {
    if (isError) {
      onClose();
    }
  }, [isError]);

  const assessment = useMemo<AssessmentPreviewType | undefined>(
    () =>
      response && {
        id: response.id,
        name: response.name,
        description: response.description,
        timeInSeconds: response.timeLimit,
        numberOfQuestions: response.questions.length,
        official: response.official,
        imageUrl: response.imageUrl,
        tags: response.tags.map((tag) => tag.name),
        // What the field made of the simulacro where enough people have sat it, and
        // the level it was published with until then. Left off until the metrics land,
        // so the badge is written once rather than corrected a moment after opening.
        difficulty: resolveDifficulty(
          response.difficultyLevel,
          metricsResponse,
          isMetricsPending,
        ),
        auditLog:
          profile.role === 'admin'
            ? response.auditLog.map((log) => ({
                icon:
                  log.action === 'created' ? (
                    <FcAddDatabase />
                  ) : log.action === 'updated' ? (
                    <FcDataBackup />
                  ) : log.action === 'deleted' ? (
                    <FcDeleteDatabase />
                  ) : (
                    <FcAcceptDatabase />
                  ),
                title:
                  log.action === 'created'
                    ? 'Simulacro creado'
                    : log.action === 'updated'
                      ? 'Simulacro actualizado'
                      : log.action === 'deleted'
                        ? 'Simulacro eliminado'
                        : 'Simulacro verificado',

                description: `Por ${log.username}`,
                date: new Date(log.date),
              }))
            : undefined,
      },
    [response, metricsResponse, isMetricsPending, profile.role],
  );

  return (
    <>
      {isLoading || !response || !assessment ? (
        <Loading />
      ) : (
        <AssessmentPreview
          assessment={assessment}
          // The same board the result card and the home cards carry, so the
          // standings and the marks they are read against say one thing wherever
          // they are read.
          scoreboard={
            metricsResponse && (
              <AssessmentScoreboard
                metrics={metricsResponse}
                ownUsername={profile.username}
                cutoffScore={response.cutoffScore}
              />
            )
          }
          onEdit={
            profile.role === 'admin' &&
            response.status !== 'removed' &&
            width > AdminWidthResolution
              ? () => {
                  setShowEdit(true);
                }
              : undefined
          }
          onViewQuestions={
            profile.role === 'admin' &&
            response.status !== 'removed' &&
            width > AdminWidthResolution &&
            response.questions.length > 0
              ? () => {
                  setShowViewQuestions(true);
                }
              : undefined
          }
          onClose={onClose}
        >
          {children}
        </AssessmentPreview>
      )}
      {response && (
        <MultipleChoiceAssessmentEditor
          open={showEdit}
          assessment={response}
          onClose={() => setShowEdit(false)}
          onDeleted={onClose}
        />
      )}
      {response && (
        <MultipleChoiceAssessmentQuestionsPreviewList
          open={showViewQuestions}
          subjectId={subjectId}
          assessmentId={assessmentId}
          questions={response.questions}
          onClose={() => setShowViewQuestions(false)}
        />
      )}
    </>
  );
}

export { MultipleChoiceAssessmentPreview };
